import { ThrowLiteError } from '../lite-error.js';
import { U32, DV, F32, U8 } from '../engine/typed-arrays.js';
import { resolveAccessor } from './gltf-parser.js';
import { resolveBufferUri } from './gltf-uri.js';
import { uploadKtx2Texture2D, decodeKtx2ImageBitmapFromBuffer } from '../texture/ktx2-loader.js';

const NAME = "KHR_texture_basisu";
const FLOAT = 5126;
const COMPONENT_BYTES = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };
const TYPE_SIZES = {
  SCALAR: 1,
  VEC2: 2,
  VEC3: 3,
  VEC4: 4,
  MAT2: 4,
  MAT3: 9,
  MAT4: 16
};
const BASISU_MATERIAL_DATA = "__basisuMaterialData";
function basisSourceIndex(tex) {
  const source = tex?.extensions?.KHR_texture_basisu?.source;
  return typeof source === "number" ? source : null;
}
function textureIndex(texInfo) {
  const index = texInfo?.index;
  return typeof index === "number" ? index : null;
}
function textureUsesBasisu(json, texInfo) {
  const index = textureIndex(texInfo);
  return index !== null && basisSourceIndex(json.textures?.[index]) !== null;
}
function stripBasisuTexture(json, owner, slot, data) {
  if (!textureUsesBasisu(json, owner?.[slot])) {
    return false;
  }
  data[slot] = owner[slot];
  delete owner[slot];
  return true;
}
function prepareBasisuMaterials(json, binChunk, baseUrl) {
  for (const mat of json.materials ?? []) {
    const data = { json, binChunk, baseUrl };
    const pbr = mat.pbrMetallicRoughness ?? {};
    let hasBasisu = stripBasisuTexture(json, pbr, "baseColorTexture", data);
    hasBasisu = stripBasisuTexture(json, pbr, "metallicRoughnessTexture", data) || hasBasisu;
    hasBasisu = stripBasisuTexture(json, mat, "normalTexture", data) || hasBasisu;
    hasBasisu = stripBasisuTexture(json, mat, "occlusionTexture", data) || hasBasisu;
    hasBasisu = stripBasisuTexture(json, mat, "emissiveTexture", data) || hasBasisu;
    const spec = mat.extensions?.KHR_materials_specular;
    if (spec) {
      hasBasisu = stripBasisuTexture(json, spec, "specularTexture", data) || hasBasisu;
      hasBasisu = stripBasisuTexture(json, spec, "specularColorTexture", data) || hasBasisu;
    }
    if (hasBasisu) {
      Object.defineProperty(mat, BASISU_MATERIAL_DATA, { value: data });
    }
  }
}
async function resolveImageBuffer(ctx, imageIdx) {
  const image = ctx.json.images?.[imageIdx];
  if (!image) {
    ThrowLiteError(116, NAME, imageIdx);
  }
  if (image.bufferView !== void 0) {
    const bv = ctx.json.bufferViews?.[image.bufferView];
    if (!bv) {
      ThrowLiteError(117, NAME, image.bufferView);
    }
    const offset = ctx.binChunk.byteOffset + (bv.byteOffset ?? 0);
    const copy = new U8(bv.byteLength);
    copy.set(new U8(ctx.binChunk.buffer, offset, bv.byteLength));
    return copy.buffer;
  }
  if (image.uri) {
    const url = resolveBufferUri(image.uri, ctx.baseUrl);
    const response = await fetch(url);
    if (!response.ok) {
      ThrowLiteError(118, NAME, response.status, response.statusText);
    }
    return response.arrayBuffer();
  }
  ThrowLiteError(119, NAME);
}
async function loadBasisuBitmap(data, texInfo) {
  const index = textureIndex(texInfo);
  if (index === null) {
    return null;
  }
  const source = basisSourceIndex(data.json.textures?.[index]);
  if (source === null) {
    return null;
  }
  data.bitmaps ??= /* @__PURE__ */ new Map();
  let bitmap = data.bitmaps.get(index);
  if (!bitmap) {
    bitmap = resolveImageBuffer(data, source).then(decodeKtx2ImageBitmapFromBuffer);
    data.bitmaps.set(index, bitmap);
  }
  return bitmap;
}
async function uploadBasisuTexture(data, ctx, texInfo, sRGB) {
  const index = textureIndex(texInfo);
  if (index === null) {
    return void 0;
  }
  data.textures ??= /* @__PURE__ */ new Map();
  const key = `${index}:${sRGB ? 1 : 0}`;
  let tex = data.textures.get(key);
  if (!tex) {
    const source = basisSourceIndex(data.json.textures?.[index]);
    if (source === null) {
      return void 0;
    }
    tex = await uploadKtx2Texture2D(ctx._engine, await resolveImageBuffer(data, source), sRGB);
    data.textures.set(key, tex);
  }
  return tex;
}
async function compositeOrm(mr, occ) {
  const w = mr.width;
  const h = mr.height;
  const c1 = new OffscreenCanvas(w, h);
  const x1 = c1.getContext("2d");
  x1.drawImage(mr, 0, 0, w, h);
  const d1 = x1.getImageData(0, 0, w, h);
  const c2 = new OffscreenCanvas(w, h);
  const x2 = c2.getContext("2d");
  x2.drawImage(occ, 0, 0, w, h);
  const d2 = x2.getImageData(0, 0, w, h);
  for (let j = 0; j < d1.data.length; j += 4) {
    d1.data[j] = d2.data[j];
  }
  x1.putImageData(d1, 0, 0);
  return createImageBitmap(c1);
}
async function uploadOrmTexture(data, ctx) {
  const mrInfo = data.metallicRoughnessTexture;
  const occInfo = data.occlusionTexture;
  const mrIndex = textureIndex(mrInfo);
  const occIndex = textureIndex(occInfo);
  if (mrIndex === null && occIndex === null) {
    return void 0;
  }
  if (mrIndex === null || occIndex === null || mrIndex === occIndex) {
    return uploadBasisuTexture(data, ctx, mrInfo ?? occInfo, false);
  }
  data.textures ??= /* @__PURE__ */ new Map();
  const key = `orm:${mrIndex}:${occIndex}`;
  let tex = data.textures.get(key);
  if (!tex) {
    const [mr, occ] = await Promise.all([loadBasisuBitmap(data, mrInfo), loadBasisuBitmap(data, occInfo)]);
    if (!mr || !occ) {
      return void 0;
    }
    tex = ctx._uploadImage(await compositeOrm(mr, occ), false);
    data.textures.set(key, tex);
  }
  return tex;
}
function readComponent(view, offset, componentType, normalized) {
  switch (componentType) {
    case FLOAT:
      return view.getFloat32(offset, true);
    case 5125: {
      const v = view.getUint32(offset, true);
      return normalized ? v / 4294967295 : v;
    }
    case 5123: {
      const v = view.getUint16(offset, true);
      return normalized ? v / 65535 : v;
    }
    case 5122: {
      const v = view.getInt16(offset, true);
      return normalized ? Math.max(v / 32767, -1) : v;
    }
    case 5121: {
      const v = view.getUint8(offset);
      return normalized ? v / 255 : v;
    }
    case 5120: {
      const v = view.getInt8(offset);
      return normalized ? Math.max(v / 127, -1) : v;
    }
    default:
      ThrowLiteError(120, NAME, componentType);
  }
}
function readStridedFloat(json, binChunk, accessorIdx) {
  const accessor = json.accessors[accessorIdx];
  const bufferView = json.bufferViews[accessor.bufferView];
  const componentType = accessor.componentType;
  const compBytes = COMPONENT_BYTES[componentType];
  if (!compBytes) {
    ThrowLiteError(121, NAME, accessorIdx, componentType);
  }
  const componentCount = TYPE_SIZES[accessor.type] ?? 1;
  const elementBytes = componentCount * compBytes;
  const byteStride = bufferView.byteStride ?? elementBytes;
  if (byteStride < elementBytes) {
    ThrowLiteError(122, NAME, byteStride, accessorIdx);
  }
  const normalized = accessor.normalized === true;
  const baseOffset = binChunk.byteOffset + (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
  const view = new DV(binChunk.buffer);
  const out = new F32(accessor.count * componentCount);
  for (let i = 0, o = 0; i < accessor.count; i++) {
    const src = baseOffset + i * byteStride;
    for (let c = 0; c < componentCount; c++, o++) {
      out[o] = readComponent(view, src + c * compBytes, componentType, normalized);
    }
  }
  return out;
}
const ext = {
  id: NAME,
  async preMesh(json, binChunk, baseUrl) {
    const gltf = json;
    prepareBasisuMaterials(gltf, binChunk, baseUrl);
    const decoded = /* @__PURE__ */ new Map();
    for (const mesh of gltf.meshes ?? []) {
      for (const primitive of mesh.primitives ?? []) {
        const attrs = primitive.attributes ?? {};
        const strided = Object.keys(attrs).some((name) => gltf.bufferViews?.[gltf.accessors?.[attrs[name]]?.bufferView]?.byteStride !== void 0);
        if (!strided) {
          continue;
        }
        const attributes = /* @__PURE__ */ new Map();
        for (const name of Object.keys(attrs)) {
          const accessorIdx = attrs[name];
          const accessor = gltf.accessors[accessorIdx];
          if (gltf.bufferViews?.[accessor.bufferView]?.byteStride !== void 0) {
            attributes.set(name, readStridedFloat(gltf, binChunk, accessorIdx));
          }
        }
        const posAcc = gltf.accessors[attrs.POSITION];
        const idx = primitive.indices === void 0 ? new U32(0) : new U32(resolveAccessor(gltf, binChunk, primitive.indices)._data);
        decoded.set(primitive, {
          _attributes: attributes,
          _indices: idx,
          _vertexCount: posAcc.count,
          _indexCount: idx.length
        });
      }
    }
    return decoded;
  },
  async applyMaterial(mat, ctx) {
    const data = mat._rawMatDef?.[BASISU_MATERIAL_DATA];
    if (!data) {
      return null;
    }
    const [baseColorTexture, ormTexture, normalTexture, emissiveTexture, specularTexture, specularColorTexture, reflectanceMod] = await Promise.all([
      uploadBasisuTexture(data, ctx, data.baseColorTexture, true),
      uploadOrmTexture(data, ctx),
      uploadBasisuTexture(data, ctx, data.normalTexture, false),
      uploadBasisuTexture(data, ctx, data.emissiveTexture, true),
      uploadBasisuTexture(data, ctx, data.specularTexture, false),
      // specularColorTexture → reflectanceTexture: load LINEAR so the GPU does not
      // sRGB-decode on sample; the reflectance shader applies its own pow(2.2)
      // (BJS toLinearSpace parity). sRGB-format here would gamma-decode twice.
      uploadBasisuTexture(data, ctx, data.specularColorTexture, false),
      // `prepareBasisuMaterials` strips the KTX2 specular textures out of the shared
      // json before gltf-ext-dielectric runs, so dielectric's `needsReflectance` never
      // sees them. Gate the opt-in setter here on the source declarations — otherwise a
      // texture-only KHR_materials_specular asset (default factors, default IOR) uploads
      // both maps but never registers the reflectance ext, so nothing samples them.
      data.specularTexture || data.specularColorTexture ? import('../material/pbr/set-metallic-reflectance.js') : void 0
    ]);
    const out = {
      ...baseColorTexture ? { baseColorTexture } : void 0,
      ...ormTexture ? {
        ormTexture,
        ...data.metallicRoughnessTexture ? { metallicFactor: mat._metallicFactor, roughnessFactor: mat._roughnessFactor } : void 0,
        ...data.occlusionTexture ? { occlusionStrength: 1, occlusionTexCoord: data.occlusionTexture.texCoord ?? 0 } : void 0
      } : void 0,
      ...normalTexture ? { normalTexture, normalTextureScale: data.normalTexture?.scale ?? 1 } : void 0,
      ...emissiveTexture ? { emissiveTexture } : void 0
    };
    if (reflectanceMod && (specularTexture || specularColorTexture)) {
      reflectanceMod.setPbrMetallicReflectance(out, {
        ...specularTexture ? { texture: specularTexture, useOnlyMetallicFromTexture: true } : void 0,
        ...specularColorTexture ? { reflectanceTexture: specularColorTexture } : void 0
      });
    }
    if (!out.baseColorTexture && !out.ormTexture && !out.normalTexture && !out.emissiveTexture && !specularTexture && !specularColorTexture) {
      return null;
    }
    return out;
  }
};

export { ext as default };
//# sourceMappingURL=gltf-ext-basisu.js.map
